import type { MessageBlock } from "@rakazo/contracts";

export interface PeerMessage {
  messageId: string;
  direction: "sent" | "received";
  peerBotId: string;
  peerBotName: string;
  text: string;
  createdAt: string;
}

export interface PeerConversation {
  peerBotId: string;
  peerBotName: string;
  messages: PeerMessage[];
  lastText: string;
  lastAt: string;
}

/** The fields a peer view reads, shared by web and mobile thread messages. */
type PeerSource = { id: string; createdAt?: string; blocks: readonly MessageBlock[] };

type PeerBlock = Extract<MessageBlock, { kind: "bot_message_sent" | "bot_message_received" }>;

export function isPeerBlock(block: MessageBlock): block is PeerBlock {
  return block.kind === "bot_message_sent" || block.kind === "bot_message_received";
}

export type PeerExchangePair = { botId: string; peerBotId: string };

/**
 * The two bots a peer receipt is between. `botId` is the bot whose own chat holds their whole
 * exchange: the open chat's bot, or in a group, where only the sender's echo appears, the
 * recipient, whose chat received every message and sent every reply.
 */
export function peerExchangePair(
  message: { botId?: string | null; blocks: readonly MessageBlock[] },
  chat: { botId?: string; groupId?: string },
): PeerExchangePair | null {
  const block = message.blocks.find(isPeerBlock);
  if (!block) return null;
  const peerBotId = block.kind === "bot_message_sent" ? block.toBotId : block.fromBotId;
  const pair = chat.groupId
    ? message.botId
      ? { botId: peerBotId, peerBotId: message.botId }
      : null
    : chat.botId
      ? { botId: chat.botId, peerBotId }
      : null;
  return pair && pair.botId !== pair.peerBotId ? pair : null;
}

export function peerMessagesFrom(messages: readonly PeerSource[]): PeerMessage[] {
  const collected: PeerMessage[] = [];
  for (const message of messages) {
    for (const block of message.blocks) {
      if (!isPeerBlock(block)) continue;
      collected.push(
        block.kind === "bot_message_sent"
          ? {
              messageId: message.id,
              direction: "sent",
              peerBotId: block.toBotId,
              peerBotName: block.toBotName,
              text: block.text,
              createdAt: message.createdAt ?? "",
            }
          : {
              messageId: message.id,
              direction: "received",
              peerBotId: block.fromBotId,
              peerBotName: block.fromBotName,
              text: block.text,
              createdAt: message.createdAt ?? "",
            },
      );
    }
  }
  return collected;
}

/** One conversation per peer, most recently active first. */
export function peerConversations(messages: readonly PeerSource[]): PeerConversation[] {
  const byPeer = new Map<string, PeerConversation>();
  for (const peerMessage of peerMessagesFrom(messages)) {
    const existing = byPeer.get(peerMessage.peerBotId);
    if (existing) {
      existing.messages.push(peerMessage);
      // Names can change; the newest one wins.
      existing.peerBotName = peerMessage.peerBotName;
      existing.lastText = peerMessage.text;
      existing.lastAt = peerMessage.createdAt;
      continue;
    }
    byPeer.set(peerMessage.peerBotId, {
      peerBotId: peerMessage.peerBotId,
      peerBotName: peerMessage.peerBotName,
      messages: [peerMessage],
      lastText: peerMessage.text,
      lastAt: peerMessage.createdAt,
    });
  }
  return [...byPeer.values()].sort((a, b) => b.lastAt.localeCompare(a.lastAt));
}
